import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

const MIN_TOUCH_TARGET = 44; // px

const CircularStepper = ({
	steps = [],
	currentStep = 0,
	onPageChange = () => { },
	isOnlyLabel = false,
	isMobileStepper: isMobileStepperProp = undefined,
	isWidthCard = undefined,
}) => {
	const { t } = useTranslation ? useTranslation() : { t: (s) => s };
	const containerRef = useRef(null);
	const [viewportWidth, setViewportWidth] = useState(
		typeof window !== "undefined" ? window.innerWidth : 1024
	);
	// Update viewport width (safe for client-side rendering)
	useEffect(() => {
		const handleResize = () => setViewportWidth(window.innerWidth);
		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	const isRTL = useMemo(() => {
		try {
			const stored = JSON.parse(localStorage.getItem("isRTL"));
			return !!stored;
		} catch {
			return false;
		}
	}, []);

	// Filtered steps (skip flagged steps)
	const filteredSteps = steps.filter((s) => !s.skip);
	const stepCount = Math.max(filteredSteps.length, 1);

	// Determine layout: if prop provided use it, otherwise calculate by width
	const isMobileLayout =
		typeof isMobileStepperProp === "boolean"
			? isMobileStepperProp
			: viewportWidth <= 720; // breakpoint: 720px

	// Base width to calculate sizes (prefer provided isWidthCard)
	const baseWidth = isWidthCard
		? isWidthCard
		: Math.max(Math.min(viewportWidth * 0.92, 1000), 320);

	// Shared colors (you can customize)
	const activeColor = "#385723";
	const completedColor = "#28a745";
	const incompleteColor = "#d3d3d3";

	// Responsive font sizes
	const numberFontSize = isMobileLayout ? 14 : 16;
	const labelFontSize = isMobileLayout ? 12 : 14;

	// ---------- CIRCULAR layout calculations ----------
	// radius safe clamped
	const circularRadius = useMemo(() => {
		if (!isMobileLayout) return Math.min(baseWidth / 6, 40);
		// On mobile, make circle large but keep min and max bounds
		const r = Math.min(baseWidth / 2.8, 55);
		return Math.max(r, 40); // ensure not too small
	}, [baseWidth, isMobileLayout]);

	const strokeWidth = isMobileLayout ? 6 : 6;
	const cx = circularRadius + strokeWidth + 8; // left padding
	const cy = circularRadius + strokeWidth + 8; // top padding

	// For circular layout: start angle at -90deg (top)
	const startAngle = -Math.PI / 2;
	const stepAngle = (2 * Math.PI) / stepCount;

	// ---------- LINEAR layout calculations ----------
	const linearHeight = 80;
	const linearPaddingX = 16;
	const linearAvailableWidth = Math.max(baseWidth - linearPaddingX * 2, 200);

	// center positions for linear
	const linearPositions = useMemo(() => {
		const positions = [];
		if (stepCount === 1) {
			positions.push(linearPaddingX + linearAvailableWidth / 2);
		} else {
			for (let i = 0; i < stepCount; i++) {
				positions.push(
					linearPaddingX + (i * linearAvailableWidth) / (stepCount - 1)
				);
			}
		}
		return positions;
	}, [linearAvailableWidth, linearPaddingX, stepCount]);

	// Helper: convert polar to cartesian
	const polarToCartesian = (centerX, centerY, radius, angle) => {
		return {
			x: centerX + radius * Math.cos(angle),
			y: centerY + radius * Math.sin(angle),
		};
	};

	// Accessible click/tap wrapper: onClick + onTouchStart
	const triggerStep = (index) => {
		if (typeof onPageChange === "function") onPageChange(index);
	};

	// Render circular step item
	const renderCircularStep = (step, index) => {
		const angle = startAngle + index * stepAngle;
		const pos = polarToCartesian(cx, cy, circularRadius, angle);

		const isActive = index === currentStep;
		const isCompleted = index < currentStep;

		// For touch targets: we render a visible circle and a larger invisible circle for tapping
		const visualR = isActive ? 12 : 10;
		const hitR = Math.max(MIN_TOUCH_TARGET / 2, visualR + 6);

		// Path arc to next step (except last)
		const nextAngle = startAngle + ((index + 1) % stepCount) * stepAngle;
		const nextPos = polarToCartesian(cx, cy, circularRadius, nextAngle);
		const largeArcFlag = stepAngle > Math.PI ? 1 : 0;

		// Arc path for segment between this step and next
		const d = `M ${pos.x} ${pos.y} A ${circularRadius} ${circularRadius} 0 ${largeArcFlag} 1 ${nextPos.x} ${nextPos.y}`;

		// For RTL: mirror shapes horizontally by flipping x coordinate around centerX
		const mirrorX = (x) => (isRTL ? cx * 2 - x : x);

		return (
			<g key={`c-step-${index}`} aria-hidden={false}>
				{/* Segment (draw all but when stepCount===1 we skip) */}
				{stepCount > 1 && index < stepCount-1 && (
					<path
						d={d}
						fill="none"
						stroke={isCompleted ? completedColor : incompleteColor}
						strokeWidth={strokeWidth}
						strokeLinecap="round"
						transform={isRTL ? `translate(${cx * 2},0) scale(-1,1)` : undefined}
						style={{ pointerEvents: "none" }}
					/>
				)}

				{/* Visible circle */}
				<circle
					cx={mirrorX(pos.x)}
					cy={pos.y}
					r={visualR}
					fill={isActive ? activeColor : isCompleted ? completedColor : incompleteColor}
					stroke="none"
					aria-label={`${index + 1} - ${step.label || ""}`}
				/>

				{/* Invisible larger hit area for touch */}
				<circle
					cx={mirrorX(pos.x)}
					cy={pos.y}
					r={hitR}
					fill="transparent"
					onClick={() => triggerStep(index)}
					style={{ cursor: "pointer", touchAction: "manipulation" }}
					aria-hidden={false}
					role="button"
					tabIndex={0}
					onKeyDown={(e) => {
						if (e.key === "Enter" || e.key === " ") triggerStep(index);
					}}
				/>

				{/* Step label (placed slightly outside circle) */}
				<text
					x={mirrorX(pos.x)}
					y={pos.y + circularRadius * 0.35}
					textAnchor="middle"
					fontSize={labelFontSize}
					fontFamily="IBMPlexSansArabic-Regular, sans-serif"
					style={{
						pointerEvents: "auto",
						cursor: "pointer",
						userSelect: "none",
						transformOrigin: `${mirrorX(pos.x)}px ${pos.y}px`,
					}}
					onClick={() => triggerStep(index)}
				>
					{step.label}
				</text>

				{/* Current step number inside the circle if active (small) */}
				
					<text
						x={mirrorX(pos.x)}
						y={pos.y + 4}
						textAnchor="middle"
						fontSize={numberFontSize}
						fontFamily="IBMPlexSansArabic-Regular, sans-serif"
						fill="#ffffff"
						style={{ pointerEvents: "none" }}
						transform={`scale(-1,1) translate(${-2 * mirrorX(pos.x)},0)`}

					>
						{index + 1}
					</text>
			</g>
			
		);
	};

	// Render linear step item
	const renderLinearStep = (step, index) => {
		const x = linearPositions[index];
		const y = linearHeight / 2;
		const isActive = index === currentStep;
		const isCompleted = index < currentStep;
		const visualR = isActive ? 12 : 10;
		const hitR = Math.max(MIN_TOUCH_TARGET / 2, visualR + 6);

		return (
			<g key={`l-step-${index}`}>
				{/* connector line to next */}
				{index < stepCount - 1 && (
					<line
						x1={x}
						y1={y}
						x2={linearPositions[index + 1]}
						y2={y}
						stroke={index < currentStep ? completedColor : incompleteColor}
						strokeWidth={4}
						strokeLinecap="round"
						style={{ pointerEvents: "none" }}
					/>
				)}

				{/* visible circle */}
				<circle
					cx={x}
					cy={y}
					r={visualR}
					fill={isActive ? activeColor : isCompleted ? completedColor : incompleteColor}
				/>

				{/* invisible larger hit area */}
				<circle
					cx={x}
					cy={y}
					r={hitR}
					fill="transparent"
					onClick={() => triggerStep(index)}
					style={{ cursor: "pointer", touchAction: "manipulation" }}
					role="button"
					tabIndex={0}
					aria-label={`${index + 1} - ${step.label || ""}`}
					onKeyDown={(e) => {
						if (e.key === "Enter" || e.key === " ") triggerStep(index);
					}}
				/>

				{/* label below */}
				<text
					x={isRTL ? baseWidth - x : x}
					y={y + 28}
					textAnchor="middle"
					fontSize={labelFontSize}
					fontFamily="IBMPlexSansArabic-Regular, sans-serif"
					style={{ cursor: "pointer", userSelect: "none" }}
					onClick={() => triggerStep(index)}
				>
					{step.label}
				</text>
			</g>
		);
	};

	// ---------- Render UI ----------
	// Only label mode
	if (isOnlyLabel) {
		const activeLabel = steps[currentStep] ? steps[currentStep].label : "";
		return (
			<div style={{ textAlign: "center", padding: 8 }}>
				<span
					style={{
						fontWeight: "bold",
						fontSize: 16,
						fontFamily: "IBMPlexSansArabic-Regular, sans-serif",
						cursor: "pointer",
					}}
					onClick={() => triggerStep(currentStep)}				>
					{activeLabel}
				</span>

			</div>
		);
	}

	// Mobile circular layout
	if (isMobileLayout) {
		return (
			<div
				ref={containerRef}
				style={{ width: "100%", display: "flex", justifyContent: "center", padding: 8 }}
				aria-hidden={false}
			>
				<svg
					width={cy * 2 + 24}
					height={cy * 2 + 24}
					viewBox={`0 0 ${cy * 2} ${cy * 2}`}
					role="img"
					aria-label={t ? t("Stepper") : "Stepper"}
					style={{ display: "block", margin: "auto" }}
				>
					{/* outer circle */}
					<circle
						cx={cx}
						cy={cy}
						r={circularRadius}
						fill="none"
						stroke={incompleteColor}
						strokeWidth={strokeWidth}
						transform={isRTL ? `translate(${cx * 2},0) scale(-1,1)` : undefined}
						style={{ pointerEvents: "none" }}
					/>

					{/* steps */}
					<g transform={isRTL ? `translate(${cx * 2},0) scale(-1,1)` : undefined}>
						{filteredSteps.map((s, i) => renderCircularStep("", i))}
					</g>

					{/* central label showing "x of N" 
					<text
						x={cx}
						y={cy + 6}
						textAnchor="middle"
						fontSize={numberFontSize}
						fontFamily="IBMPlexSansArabic-Regular, sans-serif"
						fill={completedColor}
						style={{ pointerEvents: "none", fontWeight: "600" }}
					>
						{currentStep + 1}
					</text>

					<text
						x={cx}
						y={cy + 28}
						textAnchor="middle"
						fontSize={labelFontSize}
						fontFamily="IBMPlexSansArabic-Regular, sans-serif"
						fill={completedColor}
						style={{ pointerEvents: "none" }}
					>
						{t ? `${t("of")} ${stepCount}` : `of ${stepCount}`}
					</text>
*/}
					{/* bottom active label 
					<text
						x={cx}
						y={cy + circularRadius + 22 + 25}
						textAnchor="middle"
						fontSize={labelFontSize}
						fontFamily="IBMPlexSansArabic-Regular, sans-serif"
						fill={completedColor}
						style={{ cursor: "pointer" }}
						onClick={() => triggerStep(currentStep)}
					>
						{filteredSteps[currentStep] ? filteredSteps[currentStep].label : ""}
					</text>*/}
				</svg>
			</div>
		);
	}

	// Desktop linear layout
	return (
		<div style={{ display: "flex", justifyContent: "center", width: "100%", padding: 8 }}>
			<svg
				width={baseWidth}
				height={linearHeight}
				viewBox={`0 0 ${baseWidth} ${linearHeight}`}
				role="img"
				aria-label={t ? t("Stepper linear") : "Stepper linear"}
				style={{ display: "block", margin: "auto", overflow: "visible" }}
			>
				<g transform={isRTL ? `translate(${baseWidth},0) scale(-1,1)` : undefined}>

					{filteredSteps.map((s, i) => renderLinearStep(s, i))}
				</g>
				
			</svg>
		</div>
	);
};

CircularStepper.propTypes = {
	steps: PropTypes.arrayOf(
		PropTypes.shape({
			label: PropTypes.string.isRequired,
			skip: PropTypes.bool,
		})
	).isRequired,
	currentStep: PropTypes.number,
	onPageChange: PropTypes.func,
	isOnlyLabel: PropTypes.bool,
	isMobileStepper: PropTypes.bool,
	isWidthCard: PropTypes.number,
};

export default CircularStepper;
